/**
 * resetClientState — everything account-scoped that lives outside TanStack
 * Query, wiped in one call.
 *
 * THE BUG THIS EXISTS TO KILL
 *
 * Sign-out reset exactly ONE of the nine Zustand stores. `use-auth.ts`'s
 * `SIGNED_OUT` branch — the only one in the app — wiped the query cache, reset
 * the security telemetry session and called `useAuthStore.reset()`. Nothing
 * else. So an error toast raised while account A was signed in survived the
 * sign-out, rode on top of the welcome screen, sat over the login form's back
 * button, and was still on screen after account B signed in successfully. The
 * user reads that as "the same error followed me to a different account". The
 * toast never re-fired — it never left. Error toasts are created with
 * `duration: 0` (persistent by design, `lib/toast.ts`), and nothing ever
 * cleared the queue: `toast.dismissAll()` existed and was called from nowhere.
 *
 * Toasts were the visible symptom; the leak is wider. `booking.store` and
 * `explore.store` are MMKV/localStorage-backed, so account A's draft — vehicle,
 * dates, pickup address and coordinates, promo code — survived sign-out AND an
 * app restart into account B's session. `payment.store` carries `error` and
 * `step: 'failed'`. `messaging.store` carries the active conversation and
 * per-thread draft text.
 *
 * `theme.store` is deliberately NOT reset: appearance is a device preference,
 * not account state.
 *
 * WHY A BARREL AND NOT `reset()` CALLS AT THE CALL SITE
 *
 * The same lesson the query-cache wipe already learned the hard way (see the
 * comment in `use-auth.ts`): a hardcoded list drifts. Keeping the list in one
 * exported function makes it a deliberate, code-reviewable surface, and
 * `scripts/validate-store-reset.mjs` fails the build when a store in
 * `lib/stores/` is missing from it.
 *
 * Persistence note: zustand's `persist` middleware re-writes the partialized
 * state on every `set`, so each `reset()` also overwrites the stored partition
 * with its initial values.
 *
 * THE BYTES ARE NOT STATE, AND THEY WERE NOT COVERED
 *
 * The listing wizard copies every live capture into the app's DOCUMENT directory
 * so a draft survives Android reclaiming the cache — 17 vehicle photos plus the
 * tarjeta de circulación, front and back, and any insurance policy. Clearing the
 * store drops the references; it does not delete the files. So an account that
 * signed out left another person's identity documents sitting on that device,
 * unreachable from any screen and belonging to nobody the app still knows about.
 *
 * `reset()` deliberately leaves captures alone — only the confirmed "Empezar de
 * cero" deletes a host's own work, and that rule is worth keeping. An identity
 * boundary is a different question: the draft that referenced these files was
 * just erased, so they are unreachable by construction, and they are the leaving
 * account's data. Keeping them protects nothing and exposes someone.
 */
import { toast } from '../toast'
import {
  hayQuePurgarBorradoresDe,
  marcarDuenoDeBorradores,
  olvidarDuenoDeBorradores,
} from '../storage/draft-owner'
import { useBookingStore } from './booking.store'
import { useCheckinDraftStore } from './checkin.store'
import { useExploreStore } from './explore.store'
import { useListingWizardStore } from './listing-wizard.store'
import { useMessagingStore } from './messaging.store'
import { useNotificationPrefsStore } from './notification-prefs.store'
import { usePaymentStore } from './payment.store'
import { useToursCheckoutStore } from './tours-checkout.store'
import { useToursCountryStore } from './tours-country.store'
import { useUIStore } from './ui.store'
import { pruneListingPhotoFiles } from '../utils/listing-photo-storage'
import { pruneInspectionPhotoFiles } from '../utils/inspection-photo-storage'

/**
 * POR QUE HAY UN MOTIVO, Y NO UNA SOLA PURGA PARA TODO
 *
 * `SIGNED_OUT` no es un hecho, son dos. "Cerre sesion" y "se me vencio la
 * sesion" llegan por la MISMA rama --la prueba esta en el propio `use-auth.ts`,
 * que unas lineas mas abajo hace `setSessionExpired(true)` cuando
 * `wasAuthenticated && !userInitiatedSignOut`-- y hasta hoy las dos borraban
 * todo por igual.
 *
 * Eso mataba exactamente el caso que los borradores existen para cubrir: se te
 * vence la sesion a media entrega, parado junto al carro con catorce fotos
 * tomadas, y al volver a entrar no queda nada. El borrador en disco se
 * construyo el mismo dia para que perder la app no costara nada, y nadie lo
 * cruzo con el mecanismo de autenticacion.
 *
 * LO QUE SOBREVIVE A UNA SESION VENCIDA son las dos cosas que representan
 * trabajo humano a medio hacer y que se pierden para siempre si se borran: el
 * borrador de inspeccion (quince fotos y dos firmas) y el asistente de
 * publicacion (diecisiete fotos y los papeles del carro). Todo lo demas
 * --toasts, reserva a medias, conversacion activa, estado de pago-- se limpia
 * igual, porque no es trabajo: es contexto de sesion.
 *
 * Y NADIE MAS LOS VA A VER: lo que hace seguro conservarlos es
 * `lib/storage/draft-owner.ts`, que guarda de quien son. En cada inicio de
 * sesion se compara, y si entra otra persona se purga antes de que pueda ver
 * nada. Conservar sin esa marca si seria una fuga.
 */
/**
 * Lo que queda en disco es de quien está entrando, o se va.
 *
 * ESTA ES LA MITAD QUE HACE SEGURO conservar un borrador cuando la sesión se
 * cae sola. Sin ella, la secuencia "se me venció la sesión → cerré la app →
 * entra otra persona en el mismo teléfono" le mostraría a esa persona el
 * asistente de publicación de la anterior, con diecisiete fotos y los papeles
 * del carro. `lastAuthenticatedUserId` no sirve para detectarlo: vive en
 * memoria del módulo, y ese camino pasa justamente por matar el proceso.
 *
 * VIVE ACÁ Y NO EN `use-auth.ts` por una razón concreta: borrar capturas del
 * disco es una operación que este repo mantiene deliberadamente concentrada en
 * pocos archivos revisables, y `scripts/validate-listing-draft-durability.mjs`
 * lo hace cumplir. Un cuarto lugar donde se borran fotos es exactamente lo que
 * ese guarda existe para impedir — y tenía razón: mi primera versión metió la
 * llamada en el hook de autenticación.
 *
 * La llama `use-auth.ts` en CADA inicio de sesión, incluido `INITIAL_SESSION`,
 * que es el arranque en frío: el único evento que ve la secuencia peligrosa.
 */
export function conciliarDuenoDeBorradores(userId: string): void {
  if (hayQuePurgarBorradoresDe(userId)) {
    useListingWizardStore.getState().reset()
    pruneListingPhotoFiles([])
    useCheckinDraftStore.getState().reset()
    pruneInspectionPhotoFiles([])
    olvidarDuenoDeBorradores()
  }
  marcarDuenoDeBorradores(userId)
}

export type MotivoDeCierre =
  /** Cerro sesion, o entro una identidad distinta: los bytes se van. */
  | 'salida'
  /** Se cayo el token. La misma persona vuelve enseguida: su trabajo se queda. */
  | 'sesion-vencida'

/**
 * Clear every client-side store that belongs to the signed-out account.
 *
 * Deliberately does NOT touch `auth.store` — its reset has extra sequencing
 * around `isInitializing` and stays where it is, next to the query wipe —
 * and does NOT touch `theme.store`, which is a device preference.
 */
export function resetClientState(motivo: MotivoDeCierre = 'salida'): void {
  toast.dismissAll()
  useBookingStore.getState().reset()
  useExploreStore.getState().reset()
  useMessagingStore.getState().reset()
  useNotificationPrefsStore.getState().reset()
  usePaymentStore.getState().reset()
  useToursCheckoutStore.getState().reset()
  useToursCountryStore.getState().reset()
  useUIStore.getState().reset()

  if (motivo === 'sesion-vencida') return

  useListingWizardStore.getState().reset()
  // AFTER the reset, never before: the store must stop referencing the files first, so a
  // crash between the two lines leaves a draft pointing at nothing rather than files pointing
  // at nobody. Honours the in-flight guard, so signing out mid-publish leaks bytes instead of
  // deleting a photo the upload loop has not read yet — the safe direction of that trade.
  pruneListingPhotoFiles([])
  useCheckinDraftStore.getState().reset()
  // Mismo orden y misma razon que las fotos del anuncio, unas lineas mas arriba:
  // primero se deja de referenciar, despues se borran los bytes. Un borrador de
  // inspeccion abandonado son quince fotos del carro de otra persona en el
  // telefono de quien acaba de cerrar sesion.
  pruneInspectionPhotoFiles([])
  // Ya no hay borradores, asi que tampoco hay dueno que recordar.
  olvidarDuenoDeBorradores()
}
